import { FC, useState } from 'react'

import { type Dayjs } from 'dayjs'
import styled from 'styled-components'

import CalendarIcon from 'src/Assets/Svg/common/calendar.svg?react'
import CrossIcon from 'src/Assets/Svg/common/fullCross.svg?react'
import { PrimaryDatePicker } from 'src/Components/PrimaryDatePicker'
import { formatDate } from 'src/Constants/formatDate'

const Container = styled.div`
  display: flex;
  flex-direction: column;
`

const Field = styled.div`
  display: flex;
  min-height: 56px;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 8px 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  border-radius: 16px;
  background: ${({ theme }) => theme.color.white};
`

const Trigger = styled.button`
  display: flex;
  flex: 1;
  min-width: 0;
  align-items: center;
  gap: 12px;
  padding: 0;
  border: none;
  background: none;
  font-family: 'Segoe UI';
  text-align: left;
  cursor: pointer;
`

const TriggerIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${({ theme }) => theme.color.darkGrey};
`

const Placeholder = styled.span`
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.slateGrey};
`

const Value = styled.span`
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
`

const ValueLabel = styled.span`
  font-size: 12px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.slateGrey};
`

const ValueDate = styled.span`
  font-size: 14px;
  line-height: 150%;
  color: ${({ theme }) => theme.color.darkGrey};
`

const ClearButton = styled.button`
  display: flex;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  color: ${({ theme }) => theme.color.darkGrey};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.hoverWhite};
  }
`

const PickerAnchor = styled.div`
  position: relative;
  height: 0;
`

export const RELEASE_DATE_FORMAT = formatDate.formatDate1

export interface ReleaseDateFieldProps {
  label: string
  value: Dayjs | null
  disabledDate: (date: Dayjs) => boolean
  onChange: (date: Dayjs | null) => void
}

export const ReleaseDateField: FC<ReleaseDateFieldProps> = ({
  label,
  value,
  disabledDate,
  onChange,
}) => {
  const [open, setOpen] = useState(false)

  return (
    <Container>
      <Field>
        <Trigger type='button' onClick={() => setOpen(true)}>
          {value ? (
            <Value>
              <ValueLabel>{label}</ValueLabel>
              <ValueDate>{value.format(RELEASE_DATE_FORMAT)}</ValueDate>
            </Value>
          ) : (
            <>
              <TriggerIcon>
                <CalendarIcon />
              </TriggerIcon>
              <Placeholder>{label}</Placeholder>
            </>
          )}
        </Trigger>
        {value && (
          <ClearButton
            type='button'
            aria-label={`Clear ${label.toLowerCase()}`}
            onClick={() => onChange(null)}
          >
            <CrossIcon />
          </ClearButton>
        )}
      </Field>
      <PickerAnchor>
        <PrimaryDatePicker
          open={open}
          onOpenChange={setOpen}
          value={value}
          onChange={(date) => onChange(date)}
          disabledDate={disabledDate}
          showNow={false}
          style={{ visibility: 'hidden', width: 0, height: 0, padding: 0 }}
        />
      </PickerAnchor>
    </Container>
  )
}
